import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Avatar,
  Popover,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  People as PeopleIcon,
  Timeline as TimelineIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Delete,
  Download,
  Refresh,
} from "@mui/icons-material";
import UserDataTable from "./UserDataTable";
import UserActivations from "./UserActivations";
import ConnectionAnalytics from "./ConnectionAnalytics";
import Demographics from "./Demographics";
import Trends from "./Trends";
import { uvenuFetcher } from "../../utils/uvenu-fetcher";
import { CAPTURE_SOURCES, URLS } from "../../constants";
import { connect } from "redux-zero/react";
import actions from "../../store/actions";
import { AD_TYPES } from "../AdsSetupManager/functions";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import ConfirmationDialog from "../../components/UI/ConfirmationDialog";
import { deleteEventContacts } from "../Games/networkCalls/networkCalls";
import { AppContext } from "../../contexts/App.Context";

const Index = ({ initialState, event }) => {
  const [activeFilter, setActiveFilter] = useState("All");
  const [activeTab, setActiveTab] = useState(4); // User Data tab is active
  const [searchTerm, setSearchTerm] = useState("");
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date(2025, 9)); // October 2025
  const [selectingStart, setSelectingStart] = useState(true);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);

  // Context
  const appContext = useContext(AppContext);

  // Data state
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalConnections, setTotalConnections] = useState(0);
  const [avgConnectionTime, setAvgConnectionTime] = useState("0m");
  const [growthRate, setGrowthRate] = useState("0%");
  const [sortOrder, setSortOrder] = useState("newest"); // "newest" or "oldest"

  const isDatePickerOpen = Boolean(datePickerAnchor);

  const filterOptions = [
    "All",
    "Post Event",
    "Post Game",
    "Social Login",
    "Games Polls Login",
    "Live Cam Login",
  ];

  const tabs = [
    "User Activations",
    "Connection Analytics",
    "Demographics",
    "Trends",
    "User Data",
  ];

  // Get filter options mapping for API calls
  const getFilterType = (filter) => {
    const filterMap = {
      All: "all",
      "Post Event": AD_TYPES.POST_EVENT,
      "Post Game": AD_TYPES.POST_GAME,
      "Social Login": CAPTURE_SOURCES.SOCIAL_LOGIN,
      "Games Polls Login": CAPTURE_SOURCES.GAMES_LOGIN,
      "Live Cam Login": CAPTURE_SOURCES.LIVE_CAM_LOGIN,
    };
    return filterMap[filter] || "all";
  };

  // Calculate metrics from user data
  const calculateMetrics = useCallback((contacts) => {
    // Apply date range filter to contacts for metrics calculation
    let filteredContacts = contacts;
    
    if (selectedStartDate && selectedEndDate) {
      const startOfStartDate = new Date(selectedStartDate);
      startOfStartDate.setHours(0, 0, 0, 0);
      const endOfEndDate = new Date(selectedEndDate);
      endOfEndDate.setHours(23, 59, 59, 999);
      
      filteredContacts = contacts.filter((contact) => {
        const contactDate = new Date(contact.saveDate * 1000);
        const contactDateTime = contactDate.getTime();
        const startDateTime = startOfStartDate.getTime();
        const endDateTime = endOfEndDate.getTime();
        
        return contactDateTime >= startDateTime && contactDateTime <= endDateTime;
      });
    }

    setTotalUsers(filteredContacts.length);

    // Mock calculations for other metrics - these can be replaced with real calculations
    setTotalConnections("N.A."); // N.A. for unavailable data

    // Calculate average connection time (mock calculation)
    setAvgConnectionTime("N.A.");

    // Calculate growth rate (mock calculation based on recent submissions)
    setGrowthRate("N.A.");
  }, [selectedStartDate, selectedEndDate]);

  // Fetch user data from API
  const fetchUserData = useCallback(
    async (filter = "All") => {
      if (!event?._id || !initialState?.loggedInUserObj) return;

      setLoading(true);
      try {
        const filterType = getFilterType(filter);
        const queryParams =
          filterType !== "all" ? `captureSource=${filterType}` : "";
        const url =
          URLS.SUBMIT_USERS_EMAILS(event._id) +
          (queryParams ? `?${queryParams}` : "?");

        const response = await uvenuFetcher({
          method: "GET",
          url,
          headers: {
            token: initialState.loggedInUserObj.token,
          },
        });

        if (response.statusCode === 200) {
          const contacts = response.json.contacts || [];
          setUserData(contacts);

          // Calculate metrics based on data
          calculateMetrics(contacts);
        } else {
          console.error("Failed to fetch user data:", response.json.message);
          setUserData([]);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setUserData([]);
      } finally {
        setLoading(false);
      }
    },
    [event?._id, initialState?.loggedInUserObj, calculateMetrics]
  );

  // Handle individual user deletion
  const handleDeleteUser = async (userData, index) => {
    try {
      const response = await uvenuFetcher({
        method: "DELETE",
        url: URLS.DELETE_CONTACT(
          event._id,
          userData.emailAddress,
          userData.captureSource
        ),
        headers: {
          token: initialState.loggedInUserObj.token,
        },
      });

      if (response.statusCode === 200) {
        // Remove the user from local state
        const updatedUserData = [...userData];
        updatedUserData.splice(index, 1);
        setUserData(updatedUserData);

        // Recalculate metrics
        calculateMetrics(updatedUserData);

        // Show success message (you can add a snackbar here if needed)
        console.log("Contact deleted successfully");
      } else {
        console.error("Failed to delete contact:", response.json.message);
      }
    } catch (error) {
      console.error("Error deleting contact:", error);
    }
  };

  // Handle reset data (delete all contacts)
  const resetData = async () => {
    setShowResetConfirmation(false);
    await deleteEventContacts(event._id, initialState.loggedInUserObj);
    appContext.triggerToast(true, {
      type: "success",
      message: "Event metrics reset successfully.",
    });
    await fetchUserData(activeFilter);
  };

  // Handle refresh data
  const refreshData = () => {
    fetchUserData(activeFilter);
  };

  // Handle export data to Excel
  const exportData = () => {
    let exportDataArray = [];

    // Get filter options for mapping capture source
    const filterMap = {
      [AD_TYPES.POST_EVENT]: "Post Event",
      [AD_TYPES.POST_GAME]: "Post Game",
      [CAPTURE_SOURCES.SOCIAL_LOGIN]: "Social Login",
      [CAPTURE_SOURCES.GAMES_LOGIN]: "Games Polls Login",
      [CAPTURE_SOURCES.LIVE_CAM_LOGIN]: "Live Cam Login",
    };

    filteredAndSortedUserData.forEach((item) => {
      const source = filterMap[item.captureSource] || item.captureSource;
      const saveDate = format(
        new Date(item.saveDate * 1000),
        "dd MMM yyyy hh:mm a"
      );

      let user = {
        "Email Address": item.emailAddress,
        Name: item.name || "N.A.",
        Age: item.age || "N.A.",
        "Name Consent": item.nameConsent ? "Yes" : "No",
        "Email Consent": item.emailConsent ? "Yes" : "No",
        "Terms Consent": item.termsConsent ? "Yes" : "No",
        "Capture Source": source,
        "Submitted On": saveDate,
      };
      exportDataArray.push(user);
    });

    const worksheet = XLSX.utils.json_to_sheet(exportDataArray);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const filename = event.name.replace(/\s+/g, "") + "-user_data.xlsx";
    XLSX.writeFile(workbook, filename);
  };

  // Recalculate metrics when date range changes
  useEffect(() => {
    if (userData.length > 0) {
      calculateMetrics(userData);
    }
  }, [selectedStartDate, selectedEndDate, userData, calculateMetrics]);

  // Fetch data on component mount
  useEffect(() => {
    fetchUserData(activeFilter);
  }, [fetchUserData, activeFilter]);

  // Update dynamic metric cards with real data
  const metricCards = [
    {
      title: "Total Users",
      value: totalUsers.toString(),
      icon: <PeopleIcon sx={{ fontSize: 40, color: "#24b8e7" }} />,
      color: "#24b8e7",
    },
    {
      title: "Total Connections",
      value: totalConnections.toString(),
      icon: <TimelineIcon sx={{ fontSize: 40, color: "#24b8e7" }} />,
      color: "#24b8e7",
    },
    {
      title: "Avg Connection Time",
      value: avgConnectionTime,
      icon: <ScheduleIcon sx={{ fontSize: 40, color: "#24b8e7" }} />,
      color: "#24b8e7",
    },
    {
      title: "Growth Rate",
      value: growthRate,
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: "#00c853" }} />,
      color: "#00c853",
    },
  ];

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    fetchUserData(filter);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDatePickerOpen = (event) => {
    setDatePickerAnchor(event.currentTarget);
  };

  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
    setSelectingStart(true);
  };

  const handleDateSelect = (date) => {
    if (selectingStart) {
      setSelectedStartDate(date);
      setSelectedEndDate(null);
      setSelectingStart(false);
    } else {
      if (date >= selectedStartDate) {
        setSelectedEndDate(date);
      } else {
        setSelectedStartDate(date);
        setSelectedEndDate(null);
      }
    }
  };

  const handleMonthChange = (direction) => {
    setCurrentMonth((prevMonth) => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(newMonth.getMonth() + direction);
      return newMonth;
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      const prevMonthDay = new Date(year, month, -startingDayOfWeek + i + 1);
      days.push({ date: prevMonthDay, isCurrentMonth: false });
    }

    // Add days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({ date: new Date(year, month, day), isCurrentMonth: true });
    }

    // Add days from next month to fill the grid
    const remainingCells = 42 - days.length; // 6 rows × 7 days
    for (let day = 1; day <= remainingCells; day++) {
      const nextMonthDay = new Date(year, month + 1, day);
      days.push({ date: nextMonthDay, isCurrentMonth: false });
    }

    return days;
  };

  const isDateSelected = (date) => {
    if (!selectedStartDate) return false;

    const dateTime = date.getTime();
    const startTime = selectedStartDate.getTime();

    if (selectedEndDate) {
      const endTime = selectedEndDate.getTime();
      return dateTime >= startTime && dateTime <= endTime;
    }

    return dateTime === startTime;
  };

  const isDateInRange = (date) => {
    if (!selectedStartDate || !selectedEndDate) return false;

    const dateTime = date.getTime();
    const startTime = selectedStartDate.getTime();
    const endTime = selectedEndDate.getTime();

    return dateTime > startTime && dateTime < endTime;
  };

  const formatDateRange = () => {
    if (!selectedStartDate && !selectedEndDate) return "Pick a date range";
    if (selectedStartDate && !selectedEndDate)
      return `${selectedStartDate.toLocaleDateString()} - ...`;
    if (selectedStartDate && selectedEndDate) {
      return `${selectedStartDate.toLocaleDateString()} - ${selectedEndDate.toLocaleDateString()}`;
    }
    return "Pick a date range";
  };

  // Handle lifetime data button (clear date filters)
  const handleLifetimeData = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setSelectingStart(true);
  };

  // Handle reset date range button
  const handleResetDateRange = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setSelectingStart(true);
  };

  // Handle sorting toggle
  const handleSortToggle = () => {
    setSortOrder((prevOrder) => (prevOrder === "newest" ? "oldest" : "newest"));
  };

  // Sort and filter userData based on search term, date range, and sort order
  const filteredAndSortedUserData = userData
    .filter((user) => {
      // Search term filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          user.emailAddress?.toLowerCase().includes(searchLower) ||
          user.name?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Date range filter
      if (selectedStartDate && selectedEndDate) {
        const userDate = new Date(user.saveDate * 1000);
        const startOfStartDate = new Date(selectedStartDate);
        startOfStartDate.setHours(0, 0, 0, 0);
        const endOfEndDate = new Date(selectedEndDate);
        endOfEndDate.setHours(23, 59, 59, 999);
        
        const userDateTime = userDate.getTime();
        const startDateTime = startOfStartDate.getTime();
        const endDateTime = endOfEndDate.getTime();
        
        if (userDateTime < startDateTime || userDateTime > endDateTime) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      // Sort by saveDate (timestamp)
      const dateA = a.saveDate || 0;
      const dateB = b.saveDate || 0;

      if (sortOrder === "newest") {
        return dateB - dateA; // Newest first (descending)
      } else {
        return dateA - dateB; // Oldest first (ascending)
      }
    });

  return (
    <Box
      sx={{
        backgroundColor: "#0a0b0e",
        minHeight: "100vh",
        color: "white",
        padding: 3,
        width: "100%",
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
          Event Metrics Dashboard
        </Typography>
        <Typography variant="body1" sx={{ color: "#9e9e9e" }}>
          Real-time analytics and user engagement metrics
        </Typography>
      </Box>

      {/* Filters and Date Range */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Typography
            variant="body2"
            sx={{ color: "#9e9e9e", mr: 1, alignSelf: "center" }}
          >
            Filter by Activation
          </Typography>
          {filterOptions.map((filter) => (
            <Button
              key={filter}
              variant={activeFilter === filter ? "contained" : "outlined"}
              size="small"
              onClick={() => handleFilterChange(filter)}
              sx={{
                backgroundColor:
                  activeFilter === filter ? "#24b8e7" : "transparent",
                borderColor: "#24b8e7",
                color: activeFilter === filter ? "white" : "#24b8e7",
                "&:hover": {
                  backgroundColor:
                    activeFilter === filter
                      ? "#1a94c7"
                      : "rgba(36, 184, 231, 0.1)",
                },
              }}
            >
              {filter}
            </Button>
          ))}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
            Date Range
          </Typography>
          <Button
            variant="outlined"
            startIcon={<CalendarIcon />}
            onClick={handleDatePickerOpen}
            sx={{
              borderColor: "#333",
              color: "white",
              "&:hover": {
                borderColor: "#24b8e7",
              },
            }}
          >
            {formatDateRange()}
          </Button>
        </Box>
      </Box>

      {/* Date Range Picker Popover */}
      <Popover
        open={isDatePickerOpen}
        anchorEl={datePickerAnchor}
        onClose={handleDatePickerClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#1a1b23",
            border: "1px solid #333",
            borderRadius: 2,
            mt: 1,
          },
        }}
      >
        <Box sx={{ p: 3, width: 600 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "white", fontWeight: "bold" }}
            >
              Select Date Range
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleLifetimeData}
                sx={{
                  borderColor: "#333",
                  color: "#9e9e9e",
                  "&:hover": {
                    borderColor: "#24b8e7",
                    color: "#24b8e7",
                  },
                }}
              >
                Lifetime Data
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleResetDateRange}
                sx={{
                  borderColor: "#333",
                  color: "#9e9e9e",
                  "&:hover": {
                    borderColor: "#24b8e7",
                    color: "#24b8e7",
                  },
                }}
              >
                Reset
              </Button>
            </Box>
          </Box>

          {/* Calendar Grid */}
          <Box sx={{ display: "flex", gap: 4 }}>
            {/* October 2025 */}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <IconButton
                  onClick={() => handleMonthChange(-1)}
                  sx={{ color: "#9e9e9e" }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <Typography
                  variant="subtitle1"
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  {currentMonth.toLocaleDateString("en-US", {
                    month: "long",
                    year: "numeric",
                  })}
                </Typography>
                <Box sx={{ width: 40 }} /> {/* Spacer */}
              </Box>

              {/* Days header */}
              <Grid container spacing={0} sx={{ mb: 1 }}>
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                  <Grid item xs={12 / 7} key={day}>
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 1,
                        color: "#9e9e9e",
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                      }}
                    >
                      {day}
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Calendar days */}
              <Grid container spacing={0}>
                {getDaysInMonth(currentMonth)
                  .slice(0, 35)
                  .map((dayObj, index) => {
                    const { date, isCurrentMonth } = dayObj;
                    const isSelected = isDateSelected(date);
                    const isInRange = isDateInRange(date);
                    const isToday =
                      date.toDateString() === new Date().toDateString();

                    return (
                      <Grid item xs={12 / 7} key={index}>
                        <Box
                          onClick={() =>
                            isCurrentMonth && handleDateSelect(date)
                          }
                          sx={{
                            textAlign: "center",
                            py: 1,
                            cursor: isCurrentMonth ? "pointer" : "default",
                            color: isCurrentMonth ? "white" : "#666",
                            backgroundColor: isSelected
                              ? "#24b8e7"
                              : isInRange
                              ? "rgba(36, 184, 231, 0.2)"
                              : "transparent",
                            borderRadius: isSelected ? 1 : 0,
                            fontWeight: isToday ? "bold" : "normal",
                            "&:hover": isCurrentMonth
                              ? {
                                  backgroundColor: isSelected
                                    ? "#1a94c7"
                                    : "rgba(36, 184, 231, 0.1)",
                                }
                              : {},
                          }}
                        >
                          {date.getDate()}
                        </Box>
                      </Grid>
                    );
                  })}
              </Grid>
            </Box>

            {/* November 2025 */}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box sx={{ width: 40 }} /> {/* Spacer */}
                <Typography
                  variant="subtitle1"
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  {new Date(
                    currentMonth.getFullYear(),
                    currentMonth.getMonth() + 1
                  ).toLocaleDateString("en-US", {
                    month: "long",
                    year: "numeric",
                  })}
                </Typography>
                <IconButton
                  onClick={() => handleMonthChange(1)}
                  sx={{ color: "#9e9e9e" }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>

              {/* Days header */}
              <Grid container spacing={0} sx={{ mb: 1 }}>
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                  <Grid item xs={12 / 7} key={day}>
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 1,
                        color: "#9e9e9e",
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                      }}
                    >
                      {day}
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Calendar days */}
              <Grid container spacing={0}>
                {getDaysInMonth(
                  new Date(
                    currentMonth.getFullYear(),
                    currentMonth.getMonth() + 1
                  )
                )
                  .slice(0, 35)
                  .map((dayObj, index) => {
                    const { date, isCurrentMonth } = dayObj;
                    const isSelected = isDateSelected(date);
                    const isInRange = isDateInRange(date);
                    const isToday =
                      date.toDateString() === new Date().toDateString();

                    return (
                      <Grid item xs={12 / 7} key={index}>
                        <Box
                          onClick={() =>
                            isCurrentMonth && handleDateSelect(date)
                          }
                          sx={{
                            textAlign: "center",
                            py: 1,
                            cursor: isCurrentMonth ? "pointer" : "default",
                            color: isCurrentMonth ? "white" : "#666",
                            backgroundColor: isSelected
                              ? "#24b8e7"
                              : isInRange
                              ? "rgba(36, 184, 231, 0.2)"
                              : "transparent",
                            borderRadius: isSelected ? 1 : 0,
                            fontWeight: isToday ? "bold" : "normal",
                            "&:hover": isCurrentMonth
                              ? {
                                  backgroundColor: isSelected
                                    ? "#1a94c7"
                                    : "rgba(36, 184, 231, 0.1)",
                                }
                              : {},
                          }}
                        >
                          {date.getDate()}
                        </Box>
                      </Grid>
                    );
                  })}
              </Grid>
            </Box>
          </Box>
        </Box>
      </Popover>

      {/* Metric Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {metricCards.map((metric, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                backgroundColor: "#1a1b23",
                border: "1px solid #333",
                height: "100%",
              }}
            >
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <Avatar
                  sx={{
                    backgroundColor: "transparent",
                    width: 56,
                    height: 56,
                  }}
                >
                  {metric.icon}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ color: "#9e9e9e", mb: 1 }}>
                    {metric.title}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: metric.color }}
                  >
                    {metric.value}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "#333", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            "& .MuiTab-root": {
              color: "#9e9e9e",
              "&.Mui-selected": {
                color: "#24b8e7",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#24b8e7",
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab} />
          ))}
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <UserActivations 
          userData={userData} 
          selectedStartDate={selectedStartDate}
          selectedEndDate={selectedEndDate}
        />
      )}

      {activeTab === 1 && (
        <ConnectionAnalytics 
          userData={userData} 
          selectedStartDate={selectedStartDate}
          selectedEndDate={selectedEndDate}
        />
      )}

      {activeTab === 2 && (
        <Demographics 
          userData={userData} 
          selectedStartDate={selectedStartDate}
          selectedEndDate={selectedEndDate}
        />
      )}

      {activeTab === 3 && (
        <Trends 
          userData={userData} 
          selectedStartDate={selectedStartDate}
          selectedEndDate={selectedEndDate}
        />
      )}

      {/* User Data Section */}
      {activeTab === 4 && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                User Data
              </Typography>
              <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
                Detailed user information and metrics
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#9e9e9e" }} />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: "#1a1b23",
                    color: "white",
                    "& fieldset": {
                      borderColor: "#333",
                    },
                    "&:hover fieldset": {
                      borderColor: "#24b8e7",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#24b8e7",
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleSortToggle}
                sx={{
                  color: "white",
                  backgroundColor: "#24b8e7",
                  "&:hover": {
                    backgroundColor: "#1a94c7",
                  },
                }}
              >
                {sortOrder === "newest" ? "Newest First" : "Oldest First"}
              </Button>

              {/* Action Buttons */}
              <Tooltip title="Reset all data">
                <Button
                  variant="outlined"
                  onClick={() => setShowResetConfirmation(true)}
                  startIcon={<Delete />}
                  sx={{
                    borderColor: "#24b8e7",
                    color: "#24b8e7",
                    "&:hover": {
                      backgroundColor: "rgba(36, 184, 231, 0.1)",
                      borderColor: "#24b8e7",
                    },
                  }}
                >
                  Reset Data
                </Button>
              </Tooltip>

              <Tooltip title="Refresh data">
                <Button
                  variant="outlined"
                  onClick={refreshData}
                  startIcon={<Refresh />}
                  sx={{
                    borderColor: "#24b8e7",
                    color: "#24b8e7",
                    "&:hover": {
                      backgroundColor: "rgba(36, 184, 231, 0.1)",
                      borderColor: "#24b8e7",
                    },
                  }}
                >
                  Refresh
                </Button>
              </Tooltip>

              <Tooltip title="Export data to Excel">
                <Button
                  variant="outlined"
                  onClick={exportData}
                  endIcon={<Download />}
                  sx={{
                    borderColor: "#24b8e7",
                    color: "#24b8e7",
                    "&:hover": {
                      backgroundColor: "rgba(36, 184, 231, 0.1)",
                      borderColor: "#24b8e7",
                    },
                  }}
                >
                  Export
                </Button>
              </Tooltip>
            </Box>
          </Box>

          <UserDataTable
            data={filteredAndSortedUserData}
            loading={loading}
            onDeleteUser={handleDeleteUser}
          />
        </Box>
      )}

      {/* Reset Confirmation Dialog */}
      <ConfirmationDialog
        title="Delete Confirmation"
        confirmButtonText="Yes Delete"
        message={`Are you sure you want to delete all the metrics data for this event?`}
        show={showResetConfirmation}
        onConfirm={resetData}
        onClose={() => {
          setShowResetConfirmation(false);
        }}
      />
    </Box>
  );
};

export default connect(
  (state) => ({
    initialState: state.initialState,
  }),
  actions
)(Index);
